import type { WithId } from 'fitify-types/src/types/common'
import {
  type EmailCampaign,
  EmailCampaignStatus,
} from 'fitify-types/src/types/email-campaigns'
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'

import { getCampaigns } from 'api/client'
import { useAuthContext } from 'contexts/AuthContext'
import { useCampaignsToast } from 'hooks/useCampaignsToast'

interface CampaignsState {
  campaigns: WithId<EmailCampaign>[]
  isLoading: boolean
  error: string | null
}

type CampaignsAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: WithId<EmailCampaign>[] }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'ADD_CAMPAIGN'; payload: WithId<EmailCampaign> }

const initialState: CampaignsState = {
  campaigns: [],
  isLoading: false,
  error: null,
}

const campaignsReducer = (
  state: CampaignsState,
  action: CampaignsAction
): CampaignsState => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null }
    case 'FETCH_SUCCESS':
      return { campaigns: action.payload, isLoading: false, error: null }
    case 'FETCH_ERROR':
      return { ...state, isLoading: false, error: action.payload }
    case 'ADD_CAMPAIGN':
      return { ...state, campaigns: [...state.campaigns, action.payload] }
    default:
      return state
  }
}

type CampaignsContextType = {
  campaigns: WithId<EmailCampaign>[]
  isLoading: boolean
  addCampaign: (campaign: WithId<EmailCampaign>) => void
  fetchCampaigns: () => Promise<void>
}

const CampaignsContext = createContext<CampaignsContextType | undefined>(
  undefined
)

export const CampaignsContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isLoggedIn } = useAuthContext()
  const [state, dispatch] = useReducer(campaignsReducer, initialState)

  const addCampaign = useCallback((campaign: WithId<EmailCampaign>) => {
    dispatch({ type: 'ADD_CAMPAIGN', payload: campaign })
  }, [])

  const fetchCampaigns = useCallback(async () => {
    if (!isLoggedIn) {
      return
    }

    dispatch({ type: 'FETCH_START' })
    try {
      const data = await getCampaigns()
      dispatch({ type: 'FETCH_SUCCESS', payload: data })
    } catch (error) {
      console.error(error)
      dispatch({
        type: 'FETCH_ERROR',
        payload: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [isLoggedIn])

  useEffect(() => {
    void fetchCampaigns()
  }, [fetchCampaigns])

  useCampaignsToast({ campaigns: state.campaigns })

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Auto refetch campaigns when being processed by backend
  useEffect(() => {
    const hasProcessingCampaigns = state.campaigns.some(
      (campaign) =>
        campaign.status === EmailCampaignStatus.CREATED ||
        campaign.status === EmailCampaignStatus.AUDIENCE_CREATED
    )

    if (hasProcessingCampaigns) {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          void fetchCampaigns()
        }, 5000)
      }
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [state.campaigns, fetchCampaigns])

  const value = useMemo(
    () => ({
      addCampaign,
      campaigns: state.campaigns,
      isLoading: state.isLoading,
      fetchCampaigns,
    }),
    [addCampaign, state.campaigns, state.isLoading, fetchCampaigns]
  )

  return (
    <CampaignsContext.Provider value={value}>
      {children}
    </CampaignsContext.Provider>
  )
}

export const useCampaignsContext = (): CampaignsContextType => {
  const context = useContext(CampaignsContext)
  if (!context) {
    throw new Error(
      'useCampaignsContext must be used within a CampaignsContextProvider'
    )
  }
  return context
}
